import { useState } from "react";
import { Input } from "../../components/Input";
import { ApiError, api } from "../../lib/api";
import styles from "./SetupWizard.module.css";

/** A confirmed referral code (PRD PAYWALL §1.1 wizard field): the code, which programme
 * it belongs to, and the owner's display line shown before it counts. */
export type ReferralCodeChip = {
  code: string;
  kind: "store" | "partner";
  name: string;
};

// §1.1 cardinality rule — the rejection copy is normative, verbatim.
export const PARTNER_DUPLICATE_MESSAGE = "This store already has a sales partner code.";
export const STORE_DUPLICATE_MESSAGE = "This store already has a recruiting-store code.";

const chipLine = (chip: ReferralCodeChip): string =>
  chip.kind === "partner" ? `Sales partner: ${chip.name}` : `Recruited by: ${chip.name}`;

type ReferralCodesFieldProps = {
  chips: ReferralCodeChip[];
  onChange: (chips: ReferralCodeChip[]) => void;
  /** True while typed text hasn't resolved yet — the parent holds activation until the
   * code either confirms (chips) or turns out unknown (non-blocking, §1.1). */
  onPendingChange?: (pending: boolean) => void;
  disabled?: boolean;
};

/** The §1.1 chip/add-another code field: empty → resolving → confirmed chip →
 * "+ add another code" → second instance. At most one store code and one partner code;
 * a duplicate kind is rejected inline with the exact normative copy. Zero-code users
 * see one quiet optional box. */
export const ReferralCodesField = ({
  chips,
  onChange,
  onPendingChange,
  disabled = false,
}: ReferralCodesFieldProps) => {
  const [input, setInput] = useState("");
  const [showInput, setShowInput] = useState(chips.length === 0);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async (): Promise<void> => {
    const code = input.trim();
    if (!code || resolving) return;
    setResolving(true);
    setError(null);
    try {
      const resolved = await api.resolveSalesCode(code);
      if (chips.some((c) => c.kind === resolved.kind)) {
        setError(resolved.kind === "partner" ? PARTNER_DUPLICATE_MESSAGE : STORE_DUPLICATE_MESSAGE);
        onPendingChange?.(false);
        return;
      }
      const name =
        (resolved.kind === "partner" ? resolved.display_name : resolved.store_name) ?? code;
      onChange([...chips, { code, kind: resolved.kind, name }]);
      setInput("");
      setShowInput(false);
      onPendingChange?.(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setError("code not found");
        onPendingChange?.(false);
      } else {
        setError("Couldn't verify the code. Try again.");
      }
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className={styles.field}>
      <span className={styles.label} id="referral-codes-label">
        Sales agent code
      </span>
      {chips.length > 0 && (
        <div className={styles.codeChips}>
          {chips.map((chip) => (
            <span key={chip.code} className={styles.codeChip}>
              <code className={styles.codeChipCode}>{chip.code}</code>
              <span className={styles.codeChipName}>{chipLine(chip)}</span>
              <button
                type="button"
                className={styles.codeChipRemove}
                aria-label={`Remove ${chip.code}`}
                onClick={() => {
                  const next = chips.filter((c) => c.code !== chip.code);
                  onChange(next);
                  if (next.length === 0) setShowInput(true);
                }}
                disabled={disabled}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {showInput ? (
        <Input
          aria-labelledby="referral-codes-label"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setError(null);
            onPendingChange?.(e.target.value.trim() !== "");
          }}
          onBlur={() => void confirm()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void confirm();
            }
          }}
          disabled={disabled || resolving}
          placeholder="Optional"
        />
      ) : (
        chips.length < 2 && (
          <button
            type="button"
            className={styles.textBtn}
            onClick={() => setShowInput(true)}
            disabled={disabled}
          >
            + add another code
          </button>
        )
      )}
      {resolving && <p className={styles.help}>Checking…</p>}
      {error && <p className={styles.fieldError}>{error}</p>}
      {chips.length === 0 && !error && !resolving && (
        <p className={styles.help}>Checked when you activate.</p>
      )}
    </div>
  );
};
